// src/client/renderer/shaders/outline.ts
import * as THREE from 'three';
import { VERTEX as OUTLINE_VERTEX, FRAGMENT as OUTLINE_FRAGMENT } from './outline.glsl';

export interface OutlineUniforms {
  uColor: THREE.IUniform<THREE.Color>;
  uThickness: THREE.IUniform<number>;
}

export interface OutlineOptions {
  /** Outline color. Default: black (0x000000). */
  color?: THREE.ColorRepresentation;
  /** Outline thickness in NDC units. Default: 0.02. Range: 0.005-0.08. */
  thickness?: number;
}

/** Pure shader factory (used by Plan 11-style registry consumers). */
export function createOutlineShader(): THREE.ShaderMaterial {
  const uniforms: OutlineUniforms = {
    uColor: { value: new THREE.Color(0x000000) },
    uThickness: { value: 0.02 },
  };
  return new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as Record<string, THREE.IUniform<unknown>>,
    vertexShader: OUTLINE_VERTEX,
    fragmentShader: OUTLINE_FRAGMENT,
    side: THREE.BackSide,
    depthTest: true,
  });
}

/** Plan 13 consumer surface — applies inverted-hull outline to a mesh.
 *  The mesh receives a sibling mesh sharing geometry with the outline
 *  ShaderMaterial; SkinnedMesh hosts get a bound SkinnedMesh sibling so
 *  the outline follows rig animation. On dispose() the sibling is removed.
 *  Plan 13's character system calls applyOutline(characterMesh, opts)
 *  per character; opts.color = player color, thickness = 0.02 default. */
export function applyOutline(mesh: THREE.Mesh, opts?: OutlineOptions): { dispose(): void } {
  const material = createOutlineShader();

  // Override defaults with opts
  if (opts?.color !== undefined) {
    (material.uniforms as unknown as OutlineUniforms).uColor.value = new THREE.Color(opts.color);
  }
  if (opts?.thickness !== undefined) {
    (material.uniforms as unknown as OutlineUniforms).uThickness.value = opts.thickness;
  }

  // Create sibling mesh reusing host geometry (do NOT clone — shared reference).
  const sibling =
    mesh instanceof THREE.SkinnedMesh
      ? new THREE.SkinnedMesh(mesh.geometry, material)
      : new THREE.Mesh(mesh.geometry, material);
  sibling.name = mesh.name ? `${mesh.name}:outline` : 'outline';
  sibling.renderOrder = mesh.renderOrder;
  sibling.frustumCulled = mesh.frustumCulled;
  if (mesh instanceof THREE.SkinnedMesh && sibling instanceof THREE.SkinnedMesh) {
    sibling.bind(mesh.skeleton, mesh.bindMatrix);
    sibling.bindMatrix.copy(mesh.bindMatrix);
    sibling.bindMatrixInverse.copy(mesh.bindMatrixInverse);
  }

  // Attach: to parent if one exists, otherwise as child of mesh itself
  const attachTarget: THREE.Object3D = mesh.parent ?? mesh;
  attachTarget.add(sibling);

  let disposed = false;

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      // Remove sibling from wherever it was attached
      sibling.removeFromParent();
      // Dispose ShaderMaterial only — geometry is shared with host mesh
      material.dispose();
    },
  };
}
